<template>
  <div class="voyage-track-map">
    <voyage-map-view
      v-if="assetsReady"
      ref="mapView"
      SelectedMap="default"
      :ships="mapShips"
      :Options="mapOptions"
      class="voyage-track-map-canvas"
    />
    <speed-legend
      v-show="showLegend"
      ref="legend"
      class="voyage-track-map-legend"
      :speeds="trailSpeeds"
    />
    <div v-if="overlay" class="voyage-track-map-overlay">
      <wave-loader v-if="overlay.loading" size="sm" />
      <span>{{ overlay.text }}</span>
    </div>
  </div>
</template>

<script>
import MapView from '@/views/map/index';
import SpeedLegend from '@/components/map/SpeedLegend.vue';
import { apiDatetimeToUnix } from '@/util/apiDatetime';
import { HOVER } from '@/util/chart/chartSync';

// sama dgn kanal chart voyage (syncGroup 'voyage')
const VOYAGE_HOVER = `${HOVER}:voyage`;

export default {
  name: 'VoyageTrackMap',
  components: {
    VoyageMapView: MapView,
    SpeedLegend,
  },
  props: {
    voyage: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      assetsReady: false,
      trailState: 'loading',
      trailSpeeds: [],
      trailDrawn: false,
    };
  },
  computed: {
    massId() {
      return this.voyage.mvygMassId;
    },
    // versi store biar coordinatesData hasil fetchShipTrail kebaca
    storeAsset() {
      const assets = this.$store.getters['asset/assets'] || [];
      return assets.find((asset) => String(asset.massId) === String(this.massId)) || null;
    },
    mapShips() {
      // halaman voyage selalu nampilin kapalnya, abaikan hide dari Home
      return this.storeAsset ? [{ ...this.storeAsset, isVisible: true }] : [];
    },
    mapOptions() {
      return {
        enableTrail: true,
        disableSummaryFetch: true,
        hideVoyageButton: true,
        trailRange: {
          start: apiDatetimeToUnix(this.voyage.mvygStartTime),
          end: apiDatetimeToUnix(this.voyage.mvygEndTime),
        },
        trailColorBy: 'speed',
        hoverChannel: VOYAGE_HOVER,
        routeMarkers: {
          origin: this.voyage.mvygOriginLndm || null,
          destination: this.voyage.mvygDestinationLndm || null,
        },
        dotPopupWave: true,
        onTrailState: this.onTrailState,
        fitToTrail: true,
      };
    },
    showLegend() {
      return this.trailState === 'ready' && this.trailDrawn;
    },
    overlay() {
      if (!this.assetsReady || this.trailState === 'loading') {
        return { loading: true, text: 'Loading voyage track...' };
      }
      if (this.trailState === 'unavailable') {
        return { loading: false, text: 'Track unavailable for this asset.' };
      }
      if (this.trailState === 'empty' || this.trailState === 'error') {
        return { loading: false, text: 'No GPS data in this voyage period.' };
      }
      return null;
    },
  },
  async mounted() {
    try {
      // buka detail langsung dari URL: store aset masih kosong
      if (!(this.$store.getters['asset/assets'] || []).length) {
        await this.$store.dispatch('asset/getAssetsData');
      }
    } catch (error) {
      console.error('VoyageTrackMap assets error:', error?.message);
    }
    // store dari halaman lain bisa belum ada last data
    if (this.storeAsset && !this.storeAsset.lastLatLng) {
      try {
        await this.$store.dispatch('asset/getLastData');
      } catch (error) {
        console.error('VoyageTrackMap last data error:', error?.message);
      }
    }
    if (!this.storeAsset) this.trailState = 'unavailable';
    this.assetsReady = true;
  },
  methods: {
    mapService() {
      return this.$refs.mapView ? this.$refs.mapView.mapService : null;
    },
    onTrailState(massId, state) {
      if (String(massId) !== String(this.massId)) return;
      this.trailState = state;
      const svc = this.mapService();
      const group = svc && svc.shipsLayerGroups[massId];
      this.trailDrawn = state === 'ready' && !!group && !!svc.map && svc.map.hasLayer(group);
      if (state === 'ready' && svc) {
        this.trailSpeeds = (svc.dotTrailPoints[massId] || [])
          .map((point) => point.gpsData?.speed ?? point.speed)
          .filter((speed) => Number.isFinite(Number(speed)));
        this.mountLegend(svc.map);
      }
    },
    // legend masuk pane Leaflet biar di bawah popup tapi di atas marker
    mountLegend(map) {
      const legend = this.$refs.legend && this.$refs.legend.$el;
      if (this.legendMap || !map || !legend) return;
      const { L } = window;
      const pane = map.createPane('voyageLegendPane');
      pane.style.zIndex = '650';
      pane.appendChild(legend);
      L.DomEvent.disableClickPropagation(legend);
      L.DomEvent.disableScrollPropagation(legend);
      this.legendMap = map;
      this.placeLegend = () => {
        L.DomUtil.setPosition(pane, map.containerPointToLayerPoint([map.getSize().x - 10, 10]));
      };
      map.on('move zoom viewreset resize', this.placeLegend);
      this.placeLegend();
    },
  },
  beforeDestroy() {
    if (this.legendMap) this.legendMap.off('move zoom viewreset resize', this.placeLegend);
  },
};
</script>

<style scoped>
.voyage-track-map {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 360px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

/* root Map inline height 100%, flex yg ngisi tinggi wrapper */
.voyage-track-map-canvas {
  flex: 1 1 auto;
  min-height: 0;
}

/* peta pendek: panel setting buka ke samping, bisa scroll, di atas playback */
.voyage-track-map >>> .leaflet-top.leaflet-left {
  bottom: 0;
  z-index: 1001;
}

.voyage-track-map >>> .leaflet-map-settings {
  position: static;
}

.voyage-track-map >>> .map-settings-panel {
  position: absolute;
  top: 10px;
  left: 58px;
  max-height: calc(100% - 20px);
  overflow-y: auto;
}

/* popup lebih tinggi dari peta bikin autopan dorong titiknya keluar & popup ketutup */
.voyage-track-map >>> .leaflet-popup-content {
  max-height: 230px;
  overflow-x: hidden;
  overflow-y: auto;
}

.voyage-track-map-legend {
  position: absolute;
  top: 0;
  right: 0;
  pointer-events: auto;
}

.voyage-track-map-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 600;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 12px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  font-size: 12.25px;
  color: #455a64;
  pointer-events: none;
}

@media (max-width: 991.98px) {
  .voyage-track-map {
    min-height: 340px;
  }
}

@media (max-width: 767.98px) {
  .voyage-track-map {
    min-height: 300px;
  }
}
</style>
